// App-meta store — small cross-cutting flags that don't belong to a domain:
// onboarding completion, the in-app rating prompt state, and the two "favorite"
// expense categories shown as quick-add tiles. Persisted as one KV JSON blob so
// a fresh install starts clean and a returning user isn't re-onboarded/re-nagged.

import { create } from 'zustand';

import { getDb } from '@/db';
import type { CategoryId } from '@/types/domain';

const KV_APP_META = 'app_meta';

export type Favorites = [CategoryId, CategoryId];

interface AppMetaBlob {
  onboardingDone: boolean;
  /** First-run coach-mark tutorial shown/dismissed (replayable from Więcej). */
  tutorialDone: boolean;
  /** Optional first name the user typed in onboarding — greeting uses it as-is
   *  (mianownik, never declined) so we don't invent a wrong vocative. */
  userName: string | null;
  /** "Twój miesiąc" ring center mode: false = "do końca miesiąca" (default),
   *  true = Napper-style "następna płatność za X dni ~Y zł". Persisted so the
   *  center-tap choice sticks (owner: "na stałe zmienić opcję"). */
  ringNextMode: boolean;
  /** Global text scale (owner request: "czy da się pomniejszyć/powiększyć czcionki").
   *  Multiplies every AppText size — 1 = domyślna; 0.9 mniejsza, 1.15/1.3 większa. */
  fontScale: number;
  ratingDone: boolean; // rated OR dismissed — never prompt again
  ratingValue: number | null;
  ratingFeedback: string | null;
  favorites: Favorites;
  screenViews: number; // how many screens the user has opened (for the ideas-board nudge)
  ideasHintDone: boolean; // the "discover the board" hint was shown/dismissed
  /** Per-goal list of upsell milestones (25/50/75) the user closed with X. */
  goalUpsellDismissed: Record<string, number[]>;
  /** Goal ids where the "nawet 50 zł" starter hint was dismissed. */
  goalHintDismissed: string[];
  /** Sticky A/B assignments per experiment name (local, PostHog-ready). */
  experiments: Record<string, 'A' | 'B'>;
  /** Szkoła finansów: lesson days already read (unlocks the next one). */
  schoolDoneDays: number[];
  schoolHintDone: boolean;
  /** "Włącz kopię, żeby nie stracić danych" nudge dismissed. */
  syncNudgeDone: boolean;
  /** User tapped the sync nudge while logged out -> auto-enable after login. */
  pendingSyncIntent: boolean;
  /** Screenshot share-toast: last day it was shown ('YYYY-MM-DD'). */
  shareToastDay: string | null;
  /** Opt-in: back up policy scans to the Polfinance cloud. */
  policyCloudBackup: boolean;
  /** Separate art. 9 consent: allow backing up LIFE/HEALTH policy scans. */
  policyHealthConsent: boolean;
  /** Household invite code typed BEFORE login (onboarding "Mam zaproszenie")
   *  — must survive an app restart (magic-link login), hence persisted here,
   *  not in a module variable. Consumed right after the session lands. */
  pendingHouseholdCode: string | null;
  /** Summary-shade view mode: chart of the day / list / plain-text summary. */
  startView: 'ring' | 'list' | 'text';
}

const DEFAULTS: AppMetaBlob = {
  onboardingDone: false,
  tutorialDone: false,
  userName: null,
  ringNextMode: false,
  fontScale: 1,
  ratingDone: false,
  ratingValue: null,
  ratingFeedback: null,
  favorites: ['FOOD', 'FUEL'],
  screenViews: 0,
  ideasHintDone: false,
  goalUpsellDismissed: {},
  goalHintDismissed: [],
  experiments: {},
  schoolDoneDays: [],
  schoolHintDone: false,
  syncNudgeDone: false,
  pendingSyncIntent: false,
  shareToastDay: null,
  policyCloudBackup: false,
  policyHealthConsent: false,
  pendingHouseholdCode: null,
  startView: 'ring',
};

// After this many screen opens we surface the "there's an ideas board" nudge
// on Start — long enough that the user has explored, once only.
export const IDEAS_HINT_THRESHOLD = 12;

interface AppMetaState extends AppMetaBlob {
  hydrated: boolean;
  hydrate: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  completeTutorial: () => Promise<void>;
  /** Replay the coach-mark tutorial (from Więcej). */
  restartTutorial: () => Promise<void>;
  setUserName: (name: string | null) => Promise<void>;
  setRingNextMode: (value: boolean) => Promise<void>;
  setFontScale: (value: number) => Promise<void>;
  /** Positive rating (>=4): store the value; the caller sends the user to the store. */
  setRating: (value: number, feedback?: string | null) => Promise<void>;
  /** "Może później" / low-rating handled — stop prompting either way. */
  dismissRating: () => Promise<void>;
  setFavorites: (favorites: Favorites) => Promise<void>;
  /** Count a screen open (no-op once the ideas hint is done, to stop writes). */
  bumpScreenView: () => Promise<void>;
  dismissIdeasHint: () => Promise<void>;
  dismissGoalUpsell: (goalId: string, milestone: number) => Promise<void>;
  dismissGoalHint: (goalId: string) => Promise<void>;
  setExperimentVariant: (name: string, variant: 'A' | 'B') => Promise<void>;
  markSchoolLesson: (day: number) => Promise<void>;
  dismissSchoolHint: () => Promise<void>;
  dismissSyncNudge: () => Promise<void>;
  setPendingSyncIntent: (value: boolean) => Promise<void>;
  setShareToastDay: (day: string) => Promise<void>;
  setPolicyCloudBackup: (value: boolean) => Promise<void>;
  setPolicyHealthConsent: (value: boolean) => Promise<void>;
  setPendingHouseholdCode: (code: string | null) => Promise<void>;
  setStartView: (view: 'ring' | 'list' | 'text') => Promise<void>;
}

async function persist(blob: AppMetaBlob): Promise<void> {
  const db = await getDb();
  await db.setKv(KV_APP_META, JSON.stringify(blob));
}

// INTENT: every write below snapshots the WHOLE blob (read -> patch -> persist),
// so two overlapping writes lose the slower one's fields — the route-change
// bumpScreenView clobbered pendingHouseholdCode exactly this way. The queue
// serializes read+persist+set as one unit; the patch is computed INSIDE the
// queued turn (a function of fresh state), never captured earlier.
let metaWriteChain: Promise<void> = Promise.resolve();

export const useAppMetaStore = create<AppMetaState>((set, get) => {
  const mutate = (patchOf: (s: AppMetaState) => Partial<AppMetaBlob> | null): Promise<void> => {
    const run = async () => {
      const patch = patchOf(get());
      if (patch === null) return; // no-op guards (sticky flags etc.)
      const next = snapshot(get(), patch);
      await persist(next);
      set(next);
    };
    metaWriteChain = metaWriteChain.then(run, run);
    return metaWriteChain;
  };

  return {
    hydrated: false,
    ...DEFAULTS,

    hydrate: async () => {
      const db = await getDb();
      const raw = await db.getKv(KV_APP_META);
      const blob: AppMetaBlob = raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<AppMetaBlob>) } : DEFAULTS;
      set({ hydrated: true, ...blob });
    },

    completeOnboarding: () => mutate(() => ({ onboardingDone: true })),
    completeTutorial: () => mutate(() => ({ tutorialDone: true })),
    restartTutorial: () => mutate(() => ({ tutorialDone: false })),
    setUserName: (name) => mutate(() => ({ userName: name?.trim() ? name.trim() : null })),
    setRingNextMode: (value) => mutate(() => ({ ringNextMode: value })),
    setFontScale: (value) => mutate(() => ({ fontScale: value })),
    setRating: (value, feedback = null) =>
      mutate(() => ({ ratingDone: true, ratingValue: value, ratingFeedback: feedback })),
    dismissRating: () => mutate(() => ({ ratingDone: true })),
    setFavorites: (favorites) => mutate(() => ({ favorites })),

    bumpScreenView: () =>
      mutate((s) =>
        // stop writing once the ideas hint is resolved
        s.ideasHintDone || s.screenViews > IDEAS_HINT_THRESHOLD ? null : { screenViews: s.screenViews + 1 },
      ),

    dismissIdeasHint: () => mutate(() => ({ ideasHintDone: true })),

    dismissGoalUpsell: (goalId, milestone) =>
      mutate((s) => {
        const closed = s.goalUpsellDismissed[goalId] ?? [];
        if (closed.includes(milestone)) return null;
        return { goalUpsellDismissed: { ...s.goalUpsellDismissed, [goalId]: [...closed, milestone] } };
      }),

    dismissGoalHint: (goalId) =>
      mutate((s) =>
        s.goalHintDismissed.includes(goalId) ? null : { goalHintDismissed: [...s.goalHintDismissed, goalId] },
      ),

    setExperimentVariant: (name, variant) =>
      mutate((s) =>
        // assignments are sticky
        s.experiments[name] ? null : { experiments: { ...s.experiments, [name]: variant } },
      ),

    markSchoolLesson: (day) =>
      mutate((s) =>
        s.schoolDoneDays.includes(day) ? null : { schoolDoneDays: [...s.schoolDoneDays, day].sort((a, b) => a - b) },
      ),

    dismissSchoolHint: () => mutate(() => ({ schoolHintDone: true })),
    dismissSyncNudge: () => mutate(() => ({ syncNudgeDone: true })),
    setPendingSyncIntent: (value) => mutate(() => ({ pendingSyncIntent: value })),
    setShareToastDay: (day) => mutate(() => ({ shareToastDay: day })),

    setPolicyCloudBackup: (value) =>
      // Turning the backup off also withdraws the health-data consent.
      mutate((s) => ({ policyCloudBackup: value, policyHealthConsent: value ? s.policyHealthConsent : false })),

    setPolicyHealthConsent: (value) => mutate(() => ({ policyHealthConsent: value })),

    setPendingHouseholdCode: (code) =>
      mutate(() => ({ pendingHouseholdCode: code?.trim() ? code.trim().toUpperCase() : null })),

    setStartView: (view) => mutate(() => ({ startView: view })),
  };
});

/** Pull just the persistable fields out of the live state and apply a patch. */
function snapshot(s: AppMetaState, patch: Partial<AppMetaBlob>): AppMetaBlob {
  return {
    onboardingDone: s.onboardingDone,
    tutorialDone: s.tutorialDone,
    userName: s.userName,
    ringNextMode: s.ringNextMode,
    fontScale: s.fontScale,
    ratingDone: s.ratingDone,
    ratingValue: s.ratingValue,
    ratingFeedback: s.ratingFeedback,
    favorites: s.favorites,
    screenViews: s.screenViews,
    ideasHintDone: s.ideasHintDone,
    goalUpsellDismissed: s.goalUpsellDismissed,
    goalHintDismissed: s.goalHintDismissed,
    experiments: s.experiments,
    schoolDoneDays: s.schoolDoneDays,
    schoolHintDone: s.schoolHintDone,
    syncNudgeDone: s.syncNudgeDone,
    pendingSyncIntent: s.pendingSyncIntent,
    shareToastDay: s.shareToastDay,
    policyCloudBackup: s.policyCloudBackup,
    policyHealthConsent: s.policyHealthConsent,
    pendingHouseholdCode: s.pendingHouseholdCode,
    startView: s.startView,
    ...patch,
  };
}
